import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import {
  fetchProducts,
  fetchForceData,
  runEligibilityCheck,
  forceGuarantee,
  setSelectedTransaction,
  setModalOpen,
  clearEvaluation,
  reset,
} from '../data/forceGuarantee/actions';
import {
  selectForceGuarantee,
  selectIsBusy,
  selectCanForce,
  selectHasSearched,
  selectEvaluatedId,
  selectEligibilityResults,
  selectEvaluatedTransaction,
  selectSelectedProduct,
  selectFailedRulesCount,
  selectTotalRulesCount,
  selectProductsErrorMessage,
  selectTransactionsErrorMessage,
  selectEvaluationErrorMessage,
  selectForceErrorMessage,
} from '../data/forceGuarantee/selectors';

// Read/write access to the `forceGuarantee` slice. The hooks are deliberately
// thin — every state transition and all data fetching lives in
// `src/data/forceGuarantee/`.

export const useForceGuaranteeProducts = () => {
  const { products, productsLoading } = useSelector(selectForceGuarantee);
  const productsErrorMessage = useSelector(selectProductsErrorMessage);
  return { products, productsLoading, productsErrorMessage };
};

export const useForceGuaranteeTransactions = () => {
  const {
    transactions,
    transactionsLoading,
    selectedId,
    search,
  } = useSelector(selectForceGuarantee);
  const transactionsErrorMessage = useSelector(selectTransactionsErrorMessage);
  return {
    transactions,
    transactionsLoading,
    transactionsErrorMessage,
    selectedId,
    search,
  };
};

export const useForceGuaranteeEvaluation = () => {
  const { evaluationLoading } = useSelector(selectForceGuarantee);
  const evaluationErrorMessage = useSelector(selectEvaluationErrorMessage);
  const evaluatedId = useSelector(selectEvaluatedId);
  const eligibilityResults = useSelector(selectEligibilityResults);
  return {
    evaluatedId,
    eligibilityResults,
    evaluationLoading,
    evaluationErrorMessage,
  };
};

export const useForceGuaranteeResult = () => {
  const { forceResult, forceLoading, modalOpen } = useSelector(
    selectForceGuarantee
  );
  const forceErrorMessage = useSelector(selectForceErrorMessage);
  return {
    forceResult, forceLoading, forceErrorMessage, modalOpen,
  };
};

export const useForceGuaranteeDerived = () => {
  const busy = useSelector(selectIsBusy);
  const canForce = useSelector(selectCanForce);
  const hasSearched = useSelector(selectHasSearched);
  const failedRulesCount = useSelector(selectFailedRulesCount);
  const totalRulesCount = useSelector(selectTotalRulesCount);
  const evaluatedTransaction = useSelector(selectEvaluatedTransaction);
  const selectedProduct = useSelector(selectSelectedProduct);
  return {
    busy,
    canForce,
    hasSearched,
    failedRulesCount,
    totalRulesCount,
    evaluatedTransaction,
    selectedProduct,
  };
};

export const useForceGuaranteeActions = () => {
  const dispatch = useDispatch();

  const loadProducts = useCallback(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  const searchTransactions = useCallback(
    (productCode, vin) => {
      dispatch(fetchForceData(productCode, vin));
    },
    [dispatch]
  );

  const selectTransaction = useCallback(
    id => {
      dispatch(setSelectedTransaction(id));
    },
    [dispatch]
  );

  const runEligibility = useCallback(
    orderId => {
      dispatch(runEligibilityCheck(orderId));
    },
    [dispatch]
  );

  const openModal = useCallback(() => {
    dispatch(setModalOpen(true));
  }, [dispatch]);

  const closeModal = useCallback(() => {
    dispatch(setModalOpen(false));
  }, [dispatch]);

  const confirmForce = useCallback(
    (transactionId, justification) => dispatch(forceGuarantee({ transactionId, justification })),
    [dispatch]
  );

  const backToTransactions = useCallback(() => {
    dispatch(clearEvaluation());
  }, [dispatch]);

  const resetFlow = useCallback(() => {
    dispatch(reset());
  }, [dispatch]);

  return {
    loadProducts,
    searchTransactions,
    selectTransaction,
    runEligibility,
    openModal,
    closeModal,
    confirmForce,
    backToTransactions,
    resetFlow,
  };
};
